// Note: why sales never equal bank deposits.
//
// Every claim here is structural — how card settlement, funding and cash
// collection work — or something seen first-hand with the design partner,
// who is not named. No figures, no "operators lose X%". If a sentence below
// starts to sound like a statistic, it needs a source or it comes out.

import React from "react";
import { Aside, H2, Item, List, P, Term } from "./prose";
import type { NoteBodyProps } from "../types";

export default function SalesVsDeposits({ brand }: NoteBodyProps): React.JSX.Element {
  return (
    <>
      <P>
        Pull yesterday’s sales out of your telemetry portal and write the number down. Look again in two days. For a lot of
        operators it will have moved. Then try to find that figure in the bank, and you will not — not on that day, not as
        that amount.
      </P>
      <P>
        The usual conclusion is that something is broken: the telemetry is unreliable, or the back office was keyed wrong, or
        the processor is shorting you. Usually none of that is true. Sales and deposits measure different things at
        different moments, and a day’s card sales are genuinely not final at midnight in <em>any</em> system.
      </P>

      <H2>Why the day keeps moving</H2>
      <P>
        A cashless vend is not a single event. When a customer taps, the reader asks the card network to hold a
        predetermined amount — a <Term>pre-auth hold</Term>, often more than the price of anything in the machine, because the
        reader does not yet know what will be selected. The real amount is captured afterwards, and the transaction{" "}
        <Term>settles</Term> in a batch that can land one to three days later.
      </P>
      <P>
        So the figure your portal showed for Tuesday on Wednesday morning may be a mix of settled sales and holds still
        waiting to be replaced by real amounts. When they settle, Tuesday changes. Cash adds its own lag: it only shows up
        when the machine’s <Term>DEX</Term> is read, and it reaches the bank whenever the bags are counted and deposited.
      </P>
      <Aside>
        If an old back office looked rock-steady, it was probably keyed by hand from numbers that had already settled. It
        was not more accurate. It was later.
      </Aside>

      <H2>Four reasons the deposit will never match</H2>
      <P>Even once every sale has settled, the bank deposit is a different number, for structural reasons:</P>
      <List>
        <Item title="Fees come out first.">
          Most processors fund you <em>net</em>: the deposit is sales minus that batch’s processing fees. The gross figure in
          your portal and the net figure in the bank are supposed to differ.
        </Item>
        <Item title="The batch is not the calendar day.">
          Batches close at a cutoff that is rarely midnight in your time zone, and funding lands T+1 or T+2 after that. A
          route that crosses time zones makes it worse. One deposit routinely contains parts of two days’ sales.
        </Item>
        <Item title="Cash moves on its own schedule.">
          Coin and bills are banked in lumps, days after the vends, and often by location or by route rather than by machine.
        </Item>
        <Item title="Late items net in silently.">
          Refunds, chargebacks, adjustments and monthly charges — gateway, PCI, statement fees — come out of a deposit
          without a line in your sales report to match.
        </Item>
      </List>

      <H2>What to do instead</H2>
      <P>
        Stop reconciling the calendar day. Reconcile the <Term>settlement batch</Term>: the processor’s batch report says
        exactly which transactions, gross, fees and net, make up each deposit. Keep cash and cashless on separate ledgers,
        because they settle on completely different clocks. Treat any day younger than your settlement window as provisional,
        in every report.
      </P>
      <P>
        If you are replacing your back office, ask the new vendor one question before you sign: <em>when a day you have
        already imported changes upstream, do you restate it, or only append?</em> A system that appends will quietly freeze
        Tuesday at whatever the holds said on Wednesday morning.
      </P>

      <H2>A one-hour test</H2>
      <P>
        Take one card deposit from last month. Walk it back: deposit to batch, batch to transactions, transactions to
        machines. If you can do that in an hour with the tools you already pay for, you do not need {brand.name} — you need
        that hour to become a monthly routine. If you cannot, you have learned exactly where your money becomes invisible,
        and that is where we can help.
      </P>
      <P>
        Either way, it is a better use of an hour than arguing with your telemetry provider about a number that was never
        going to be final.
      </P>
    </>
  );
}
